import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  PaginationConfig,
  PricingGeoActions,
  PricingTierActions,
  PricingTierPayload,
  PricingTierState,
  UntilDestroy,
  selectGeoPricingTiers,
  selectLoadedPricingGeoData,
  selectPricingTier,
  untilDestroyed,
} from '@shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import * as _ from 'lodash';
import { Observable, combineLatest, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import {
  GeoLocation,
  GeoPricingTier,
  GeoPricingTierLists,
  ModalService,
  PaginationService,
  PricingTier,
} from 'thkee-common';
import { ProcessingDirective } from '../../../../../../../thkee-common/src/lib/directives/processing.directive';
import { CommonPaginationComponent } from '../../../../@shared/components/common-pagination/common-pagination.component';
import { DataTableComponent } from '../../../../@shared/components/data-table/data-table.component';

interface geoPricingTiersTable {
  id: string | number;
  name: string;
  percentage: string | number;
  tierAmount: string | number;
  geoAmount: string | number;
  status: boolean | undefined;
  currency_code: string;
}
const pageSize: number = 10;

@UntilDestroy()
@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  imports: [
    AngularSvgIconModule,
    ProcessingDirective,
    NgTemplateOutlet,
    CommonPaginationComponent,
    DataTableComponent,
    AsyncPipe,
  ],
})
export class PriceGeolocationViewComponent implements OnInit {
  private store = inject(Store);
  private readonly modalService = inject(ModalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private paginationService = inject(PaginationService);

  geoGeoData!: GeoLocation; // TODO: Update or replace this with real data
  geoTiersData!: GeoPricingTier[];

  id = '';
  priceGeo$: Observable<GeoLocation> = this.store.select(selectLoadedPricingGeoData);
  priceGeoDataDefault: GeoLocation = {
    id: 0,
    date_created: '',
    date_updated: '',
    status: false,
    is_deleted: false,
    country: 0,
    currency: 0,
    country_str: '',
    currency_str: '',
    currency_convert: false,
  };
  formData: any = {};
  hasData = false;

  // Pricing Tiers
  priceTiers$: Observable<PricingTierState> = this.store.select(selectPricingTier);
  priceTiers!: PricingTier[];

  // Geo Pricing Tiers
  geoPricingTiers$: Observable<GeoPricingTierLists> = this.store.select(selectGeoPricingTiers);
  geoPricingTiers!: GeoPricingTier[];
  geoPricingTiersCount: number = 0;

  // Geo Tiers Data
  geoPricingTiersTable!: geoPricingTiersTable[];

  isLoading: boolean = true;
  queryParams: PricingTierPayload = { geo_location: 0, page: 1, page_size: pageSize };
  paginationConfig: PaginationConfig = {
    pageCountSummary: true,
    pageSize: true,
    firstAndLast: true,
  };

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    this.store.dispatch(PricingGeoActions.getGeoLoc({ id: this.id }));

    // Get Geo Location Data with ID then fetch tiers and geo tiers
    this.priceGeo$
      .pipe(
        untilDestroyed(this),
        switchMap((item): any => {
          // Handle the initial data
          this.geoGeoData = item;
          this.formData = item;
          this.hasData = true;

          if (item.id) {
            // Dispatch the action only if geo_pricing.id is available
            this.queryParams.geo_location = item.id;
            this.store.dispatch(PricingTierActions.loadPricingTierList({ page: 1, page_size: 100 }));
            this.queryUpdater();
          }
          return of(item);
        })
      )
      .subscribe();

    // Pagination
    this.geoPricingTiers$.subscribe((item) => {
      this.paginationService.getPager(item?.count, this.queryParams.page, this.queryParams.page_size);
      if (item?.count > 0) {
        this.isLoading = false;
      }
    });

    // forkJoin([
    //   this.priceTiers$.pipe(
    //     untilDestroyed(this),
    //     tap((priceTiers: PricingTierState) => {
    //       this.priceTiers = priceTiers?.pricingTierList?.results || [];
    //       console.log('Q1--', this.priceTiers);
    //     })
    //   ),
    //   this.geoPricingTiers$.pipe(
    //     untilDestroyed(this),
    //     tap((geoPricingTier: GeoPricingTier[]) => {
    //       this.geoPricingTiers = geoPricingTier || [];
    //       console.log('Q2--', this.geoPricingTiers);
    //     })
    //   ),
    // ]).subscribe();

    combineLatest([
      this.priceTiers$.pipe(
        untilDestroyed(this),
        tap((priceTiers) => {
          this.priceTiers = priceTiers?.pricingTierList?.results || [];
          return this.priceTiers;
        }),
        map(() => this.priceTiers)
      ),
      this.geoPricingTiers$.pipe(untilDestroyed(this)),
    ]).subscribe(([priceTiers, geoPricingTier]) => {
      this.geoPricingTiersTable = [];
      this.geoPricingTiersCount = geoPricingTier.count;
      if (geoPricingTier) {
        geoPricingTier.results.forEach((gTier) => {
          const sTier = _.find(priceTiers, { id: gTier.pricing_tier });
          if (sTier) {
            // Compute
            let originalPrice = Number(sTier?.price);
            let tierPrice: number = 0;
            tierPrice = originalPrice + (originalPrice * Number(gTier.percentage)) / 100;
            const data: geoPricingTiersTable = {
              id: gTier.id,
              name: sTier?.name || 'Not Found',
              percentage: Math.round(Number(gTier.percentage)),
              tierAmount: sTier ? Number(sTier?.price).toFixed(2) : '',
              geoAmount: tierPrice.toFixed(2),
              status: sTier?.price_tier_status || false,
              currency_code: sTier['currency_code'] ?? 'USD',
            };
            this.geoPricingTiersTable.push(data);
          }
        });
      }
    });

    // TODO: Soon to be deprecated please convert to latest endpoint
    // this.store.dispatch(PricingTierActions.loadPricingTierList({ page: 1, page_size: 10 }));
    // combineLatest([this.priceTiers$, this.priceGeo$])
    //   .pipe(untilDestroyed(this))
    //   .subscribe(([pricingTiers, geoLocation]) => {
    //     try {
    //       this.priceTiers = [...pricingTiers?.pricingTierList?.results] || [];
    //     } catch (e) {}

    //     console.log('C1--', this.priceTiers, geoLocation);

    //     this.geoGeoData = geoLocation;
    //     this.formData = geoLocation;
    //     this.hasData = true;
    //     if (geoLocation.geo_pricing) {
    //       const geoPricingId = geoLocation.geo_pricing.id;
    //       if (geoPricingId) {
    //         // this.store.dispatch(PricingGeoActions.loadGeoPricingTiers({ geo_pricing: geoPricingId }));
    //       }
    //     }
    //   });
  }

  editMode() {
    this.modalService.confirm({
      title: 'Edit Confirmation',
      message: `Are you sure want to edit Geolocation for ${this.geoGeoData.country_str}?`,
      onConfirm: () => {
        this.router.navigate([this.router.url + '/edit']);
      },
    });
  }

  // Pagination config
  private queryUpdater() {
    firstValueFrom(this.route.queryParams).then((params) => {
      this.queryParams.page = params['page'] ? +params['page'] : 1;
      this.queryParams.page_size = params['page_size'] ? +params['page_size'] : 10;
      this.store.dispatch(PricingGeoActions.loadGeoPricingTiers(this.queryParams));
    });
  }

  pageSizeAction(value: number) {
    this.isLoading = true;
    this.queryParams.page_size = value;
    this.queryParams.page = 1;
    this.store.dispatch(PricingGeoActions.loadGeoPricingTiers(this.queryParams));
  }

  paginationAction(page_number: number) {
    this.isLoading = true;
    this.queryParams.page = page_number;
    this.store.dispatch(PricingGeoActions.loadGeoPricingTiers(this.queryParams));
  }
}
